'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { History, Trash2, ChevronRight, TrendingUp, X, Download } from 'lucide-react';
import type { RateInput, RateBreakdown } from '@/lib/rateCalculator';
import { formatCurrency } from '@/lib/rateCalculator';
import { useLanguage } from '@/context/LanguageContext';

export interface PastCalculation {
  id: string;
  timestamp: number;
  input: RateInput;
  breakdown: RateBreakdown;
}

const STORAGE_KEY = 'vv18_past_calculations';
const MAX_HISTORY = 20;

export function savePastCalculation(input: RateInput, breakdown: RateBreakdown): void {
  if (typeof window === 'undefined') return;
  const existing: PastCalculation[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const entry: PastCalculation = {
    id: `calc-${Date.now()}`,
    timestamp: Date.now(),
    input,
    breakdown,
  };
  const updated = [entry, ...existing].slice(0, MAX_HISTORY);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
}

export function loadPastCalculations(): PastCalculation[] {
  if (typeof window === 'undefined') return [];
  return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
}

const platformColors: Record<string, string> = {
  instagram: 'text-pink-400 bg-pink-400/10 border-pink-400/30',
  tiktok: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
  youtube: 'text-red-400 bg-red-400/10 border-red-400/30',
};

const platformIcons: Record<string, string> = {
  instagram: 'IG',
  tiktok: 'TK',
  youtube: 'YT',
};

function exportToCSV(calculations: PastCalculation[]): void {
  if (calculations.length === 0) return;

  const headers = [
    'Date', 'Time', 'Platform', 'Content Type', 'Niche', 'Followers',
    'Engagement Rate (%)', 'Audience Tier', 'Engagement Status', 'Usage Rights',
    'Exclusivity', 'Base Rate ($)', 'Engagement Multiplier', 'Niche Premium (%)',
    'Content Type Multiplier', 'Usage Rights Add-on (%)', 'Exclusivity Add-on (%)',
    'Estimated Reach', 'Suggested Low ($)', 'Suggested Mid ($)', 'Suggested High ($)', 'Final Rate ($)',
  ];

  const rows = calculations.map((calc) => {
    const date = new Date(calc.timestamp);
    const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    const { input, breakdown } = calc;
    return [
      dateStr, timeStr,
      input.platform.charAt(0).toUpperCase() + input.platform.slice(1),
      input.contentType, input.niche, input.followers, input.engagementRate,
      breakdown.audienceTier, breakdown.engagementStatus,
      input.usageRights ? 'Yes' : 'No', input.exclusivity ? 'Yes' : 'No',
      breakdown.baseRate, breakdown.engagementMultiplier,
      Math.round(breakdown.nichePremium * 100), breakdown.contentTypeMultiplier,
      Math.round(breakdown.usageRightsAddon * 100), Math.round(breakdown.exclusivityAddon * 100),
      breakdown.estimatedReach, breakdown.suggestedLow, breakdown.suggestedMid,
      breakdown.suggestedHigh, breakdown.finalRate,
    ];
  });

  const csvContent = [headers, ...rows]
    .map((row) =>
      row.map((cell) => {
        const str = String(cell);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    ).join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const now = new Date();
  const filename = `voltvision_rate_history_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.csv`;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

interface PastCalculationsSidebarProps {
  onRestore?: (input: RateInput) => void;
  refreshTrigger?: number;
}

export default function PastCalculationsSidebar({ onRestore, refreshTrigger }: PastCalculationsSidebarProps) {
  const [calculations, setCalculations] = useState<PastCalculation[]>([]);
  const [isOpen, setIsOpen] = useState(true);
  const [, setTick] = useState(0);
  const { t } = useLanguage();

  const reload = useCallback(() => {
    setCalculations(loadPastCalculations());
  }, []);

  useEffect(() => {
    reload();
  }, [reload, refreshTrigger]);

  useEffect(() => {
    const interval = setInterval(() => setTick((tick) => tick + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  function timeAgo(timestamp: number): string {
    const diff = Date.now() - timestamp;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return t.history.justNow;
    if (mins < 60) return `${mins}${t.history.minutesAgo}`;
    if (hours < 24) return `${hours}${t.history.hoursAgo}`;
    return `${days}${t.history.daysAgo}`;
  }

  const clearAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setCalculations([]);
  };

  const removeOne = (id: string) => {
    const updated = calculations.filter((c) => c.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setCalculations(updated);
  };

  const maxRate = calculations.length > 0 ? Math.max(...calculations.map((c) => c.breakdown.finalRate)) : 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center">
            <History size={13} className="text-primary" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">{t.history.title}</div>
            <div className="text-[10px] text-muted-foreground">
              {calculations.length} {calculations.length !== 1 ? t.history.calculations_plural : t.history.calculations}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {calculations.length > 0 && (
            <button
              onClick={() => exportToCSV(calculations)}
              className="p-1.5 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
              title={t.history.exportCSV}
            >
              <Download size={13} />
            </button>
          )}
          {calculations.length > 0 && (
            <button
              onClick={clearAll}
              className="p-1.5 rounded-md text-muted-foreground hover:text-danger hover:bg-danger/10 transition-colors"
              title={t.history.clearAll}
            >
              <Trash2 size={13} />
            </button>
          )}
          <button
            onClick={() => setIsOpen((v) => !v)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
            title={isOpen ? t.history.collapse : t.history.expand}
          >
            <ChevronRight size={13} className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
          </button>
        </div>
      </div>

      {/* Body */}
      {isOpen && (
        <div className="flex-1 overflow-y-auto">
          {calculations.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 px-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-muted/30 border border-border flex items-center justify-center">
                <History size={20} className="text-muted-foreground/50" />
              </div>
              <div>
                <p className="text-sm font-semibold text-muted-foreground">{t.history.empty}</p>
                <p className="text-xs text-muted-foreground/70 mt-1">{t.history.emptyDesc}</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-border/40">
              {calculations.map((calc, index) => {
                const isHighest = calc.breakdown.finalRate === maxRate && maxRate > 0;
                const platformClass = platformColors[calc.input.platform] || 'text-muted-foreground bg-muted/20 border-border';
                const prevCalc = calculations[index + 1];
                const rateChange = prevCalc ? calc.breakdown.finalRate - prevCalc.breakdown.finalRate : null;

                return (
                  <div key={calc.id} className="group px-4 py-3 hover:bg-muted/20 transition-colors">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2.5 flex-1 min-w-0">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg border text-[10px] font-extrabold flex items-center justify-center ${platformClass}`}>
                          {platformIcons[calc.input.platform]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-extrabold font-tabular text-foreground">
                              {formatCurrency(calc.breakdown.finalRate)}
                            </span>
                            {isHighest && (
                              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/25">
                                {t.history.best}
                              </span>
                            )}
                            {rateChange !== null && rateChange !== 0 && (
                              <span className={`text-[10px] font-bold flex items-center gap-0.5 ${rateChange > 0 ? 'text-positive' : 'text-danger'}`}>
                                <TrendingUp size={9} className={rateChange < 0 ? 'rotate-180' : ''} />
                                {rateChange > 0 ? '+' : ''}{formatCurrency(Math.abs(rateChange))}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                            {t.contentTypes[calc.input.contentType]} · {t.niches[calc.input.niche]}
                          </div>
                          <div className="text-[10px] text-muted-foreground/60 mt-0.5">
                            {(calc.input.followers / 1000).toFixed(1)}K {t.history.followers} · {calc.input.engagementRate}% {t.history.eng}
                          </div>
                          <div className="text-[10px] text-muted-foreground/50 mt-1">
                            {timeAgo(calc.timestamp)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <button
                          onClick={() => removeOne(calc.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded text-muted-foreground hover:text-danger hover:bg-danger/10 transition-all"
                          title={t.history.remove}
                        >
                          <X size={11} />
                        </button>
                        {onRestore && (
                          <button
                            onClick={() => onRestore(calc.input)}
                            className="opacity-0 group-hover:opacity-100 text-[9px] font-bold text-primary hover:underline transition-all whitespace-nowrap"
                            title={t.history.restore}
                          >
                            {t.history.restore}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Footer trend summary */}
      {isOpen && calculations.length >= 2 && (
        <div className="px-4 py-3 border-t border-border/60 bg-muted/10">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground font-medium">{t.history.rateTrend}</span>
            <div className="flex items-center gap-1">
              {(() => {
                const latest = calculations[0].breakdown.finalRate;
                const oldest = calculations[calculations.length - 1].breakdown.finalRate;
                const diff = latest - oldest;
                const pct = Math.round((diff / oldest) * 100);
                return (
                  <span className={`text-[10px] font-bold flex items-center gap-0.5 ${diff >= 0 ? 'text-positive' : 'text-danger'}`}>
                    <TrendingUp size={10} className={diff < 0 ? 'rotate-180' : ''} />
                    {diff >= 0 ? '+' : ''}{pct}% {t.history.overall}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
